import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Button,
  Stack,
  Grid,
  Alert,
  Autocomplete,
  AutocompleteOption,
  CircularProgress,
  Box,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/joy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/api';
import { refreshJobViews } from '../../lib/refreshJobViews';
import type { BoardStatus, JobDetail, ParsedAddress } from '../../types';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { useAuth } from '../../contexts/AuthContext';
import { TagPicker } from '../tags/TagPicker';
import { AddressAutocompleteInput } from '../common/AddressAutocompleteInput';
import { applyPrefill, contactAddressFields } from '../../utils/prefill';
import { Trash as DeleteIcon } from '@phosphor-icons/react';

interface EditJobModalProps {
  open: boolean;
  onClose: () => void;
  job: JobDetail | null;
  onSuccess?: () => void;
}

interface JobUpdateData {
  name?: string;
  claim_number?: string;
  gate_code?: string;
  gated_community?: boolean | null;
  insurance_company?: string;
  policy_number?: string;
  // Nullable on the API: the typed columns reject '' when cleared
  deductible_amount?: string | null;
  date_of_loss?: string | null;
  adjuster_name?: string;
  adjuster_email?: string;
  adjuster_phone?: string;
  description?: string;
  address_1: string;
  address_2?: string;
  city: string;
  state: string;
  zip_code: string;
  value?: number;
  confidence?: number;
  board: number;
  board_status: number;
  associated_contact?: number;
  build_date?: string;
  // sales_rep?: number;
  assigned_to?: number;  // Used for form state
  assigned_to_id?: number | null;  // Sent to API
  tag_ids?: number[];
}

// interface User {
//   id: number;
//   email: string;
//   first_name: string;
//   last_name: string;
//   full_name?: string;
// }

export const EditJobModal: React.FC<EditJobModalProps> = ({
  open,
  onClose,
  job,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { users, loading: usersLoading } = useOrganizationUsers();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [dirty, setDirty] = React.useState(false);
  const confirmClose = () => { if (!dirty || window.confirm('Discard unsaved job changes?')) { setDirty(false); onClose(); } };
  React.useEffect(() => { if (open) setDirty(false); }, [open]);
  React.useEffect(() => {
    if (!open || !dirty) return;
    const protect = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, [open, dirty]);
  const [formData, setFormData] = useState<JobUpdateData>({
    name: '',
    claim_number: '',
    insurance_company: '',
    gate_code: '',
    gated_community: null,
    policy_number: '',
    deductible_amount: '',
    date_of_loss: '',
    adjuster_name: '',
    adjuster_email: '',
    adjuster_phone: '',
    description: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    value: undefined,
    confidence: undefined,
    board: 0,
    board_status: 0,
    associated_contact: undefined,
    build_date: undefined,
    // sales_rep: undefined,
    assigned_to: undefined,
  });

  const [contactSearch, setContactSearch] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  // Fetch all tags
  const { data: availableTags = [], refetch: refetchTags } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await apiClient.getTags();
      return response.data;
    },
  });

  // Handle creating new tags
  const handleCreateTag = async (name: string, color: string) => {
    const response = await apiClient.createTag(name, color);
    await refetchTags();
    return response.data;
  };

  // Reset form when job changes
  useEffect(() => {
    if (job && open) {
      setFormData({
        name: job.name || '',
        claim_number: job.claim_number || '',
        insurance_company: job.insurance_company || '',
        gate_code: job.gate_code || '',
        gated_community: job.gated_community ?? null,
        policy_number: job.policy_number || '',
        deductible_amount: job.deductible_amount || '',
        date_of_loss: job.date_of_loss || '',
        adjuster_name: job.adjuster_name || '',
        adjuster_email: job.adjuster_email || '',
        adjuster_phone: job.adjuster_phone || '',
        description: job.description || '',
        address_1: job.address_1,
        address_2: job.address_2 || '',
        city: job.city,
        state: job.state,
        zip_code: job.zip_code,
        value: job.value ?? undefined,
        confidence: job.confidence ?? undefined,
        board: job.board,
        board_status: job.board_status,
        associated_contact: job.associated_contact?.id,
        build_date: job.build_date || undefined,
        // sales_rep: typeof job.sales_rep === 'object' ? job.sales_rep?.id : job.sales_rep,
        assigned_to: typeof job.assigned_to === 'object' ? job.assigned_to?.id : job.assigned_to,
      });
      // Set initial contact search to show current contact
      if (job.associated_contact) {
        setContactSearch(`${job.associated_contact.first_name} ${job.associated_contact.last_name}`);
      }
      // Set initial tags
      setSelectedTagIds(job.tags ? job.tags.map(tag => tag.id) : []);
    }
  }, [job, open]);

  // Fetch boards for selection
  const { data: boards = [] } = useQuery({
    queryKey: ['boards'],
    queryFn: async () => {
      const response = await apiClient.getBoards();
      return response.data;
    },
  });

  // Fetch statuses for selected board
  const { data: statuses = [] } = useQuery({
    queryKey: ['board-statuses', formData.board],
    queryFn: async () => {
      if (!formData.board) return [];
      const response = await apiClient.getAllPages<BoardStatus>(`/api/board-statuses/by-board/${formData.board}/`);
      // Ensure we always return an array
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!formData.board,
  });

  // Fetch contacts for dropdown with search
  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', contactSearch],
    queryFn: async () => {
      let url = `/api/crm/contacts/`;
      const params = [];
      
      if (contactSearch) {
        params.push(`search=${encodeURIComponent(contactSearch)}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      const response = await apiClient.get<any>(url);
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },
    staleTime: 30000,
    enabled: open, // Only fetch when modal is open
  });

  // TODO: Add user assignment functionality when users API endpoint is available

  // Update job mutation
  const updateMutation = useMutation({
    mutationFn: async (data: JobUpdateData) => {
      if (!job) throw new Error('No job to update');
      
      // Only send fields that have changed
      const changedFields: Partial<JobUpdateData> = {};
      
      if ((data.name ?? '') !== (job.name || '')) changedFields.name = data.name ?? '';

      // Compare each field with original job data
      if (data.claim_number !== (job.claim_number || '')) changedFields.claim_number = data.claim_number;
      // handleSubmit strips '' from the payload, so an erased claim field
      // arrives here as undefined - `?? ''` on both sides is what tells a
      // genuine clear apart from "unchanged and empty", and what makes the
      // clear actually persist. deductible/date are typed columns that reject
      // '', hence null.
      if ((data.insurance_company ?? '') !== (job.insurance_company || '')) changedFields.insurance_company = data.insurance_company ?? '';
      if ((data.gate_code ?? '') !== (job.gate_code || '')) changedFields.gate_code = data.gate_code ?? '';
      if ((data.gated_community ?? null) !== (job.gated_community ?? null)) changedFields.gated_community = data.gated_community ?? null;
      if ((data.policy_number ?? '') !== (job.policy_number || '')) changedFields.policy_number = data.policy_number ?? '';
      if ((data.deductible_amount ?? '') !== (job.deductible_amount || '')) changedFields.deductible_amount = data.deductible_amount || null;
      if ((data.date_of_loss ?? '') !== (job.date_of_loss || '')) changedFields.date_of_loss = data.date_of_loss || null;
      if ((data.adjuster_name ?? '') !== (job.adjuster_name || '')) changedFields.adjuster_name = data.adjuster_name ?? '';
      if ((data.adjuster_email ?? '') !== (job.adjuster_email || '')) changedFields.adjuster_email = data.adjuster_email ?? '';
      if ((data.adjuster_phone ?? '') !== (job.adjuster_phone || '')) changedFields.adjuster_phone = data.adjuster_phone ?? '';
      if (data.description !== (job.description || '')) changedFields.description = data.description;
      if (data.address_1 !== job.address_1) changedFields.address_1 = data.address_1;
      if (data.address_2 !== (job.address_2 || '')) changedFields.address_2 = data.address_2;
      if (data.city !== job.city) changedFields.city = data.city;
      if (data.state !== job.state) changedFields.state = data.state;
      if (data.zip_code !== job.zip_code) changedFields.zip_code = data.zip_code;
      if (data.value !== job.value) changedFields.value = data.value;
      if (data.confidence !== job.confidence) changedFields.confidence = data.confidence;
      if (data.board !== job.board) changedFields.board = data.board;
      if (data.board_status !== job.board_status) changedFields.board_status = data.board_status;
      if (data.associated_contact !== job.associated_contact?.id) changedFields.associated_contact = data.associated_contact;
      if (data.build_date !== (job.build_date || undefined)) changedFields.build_date = data.build_date;
      
      // Handle user assignment fields
      // const originalSalesRepId = typeof job.sales_rep === 'object' ? job.sales_rep?.id : job.sales_rep;
      // if (data.sales_rep !== originalSalesRepId) changedFields.sales_rep = data.sales_rep;

      const originalAssignedToId = typeof job.assigned_to === 'object' ? job.assigned_to?.id : job.assigned_to;
      if (data.assigned_to !== originalAssignedToId) {
        // Send assigned_to_id to backend (write-only field)
        changedFields.assigned_to_id = data.assigned_to ?? null;
      }

      // Always send tag_ids if provided (to support adding/removing tags)
      if (data.tag_ids !== undefined) {
        changedFields.tag_ids = data.tag_ids;
      }

      const response = await apiClient.patch(`/api/jobs/${job.id}/`, changedFields);
      return response.data;
    },
    onSuccess: async () => {
      if (job) await refreshJobViews(queryClient, job.id);
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      // Handle error silently
    },
  });

  // Delete job mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!job) throw new Error('No job to delete');
      const response = await apiClient.deleteJob(job.id);
      return response;
    },
    onSuccess: () => {
      // Invalidate queries and navigate back to board
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      onClose();
      navigate('/');
    },
    onError: (error: any) => {
      // Handle error silently
    },
  });

  const changeFormData: typeof setFormData = (value) => { setDirty(true); setFormData(value); };
  const handleAddressSelect = (parsed: ParsedAddress) => {
    changeFormData(prev => ({
      ...prev,
      address_1: parsed.address_1 || prev.address_1,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      zip_code: parsed.zip_code || prev.zip_code,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.address_1 || !formData.city || !formData.state || !formData.zip_code || !formData.board || !formData.board_status) {
      return;
    }

    // Clean up the data before sending - remove empty strings
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value !== '') {
        (acc as any)[key] = value;
      }
      return acc;
    }, {} as JobUpdateData);
    cleanedData.name = formData.name?.trim() || '';

    // Always include tag_ids (even if empty) to support adding/removing/clearing tags
    cleanedData.tag_ids = selectedTagIds;

    updateMutation.mutate(cleanedData);
  };

  // Helper function to format error messages
  const getErrorMessage = (error: any): string => {
    if (error?.responseData) {
      // Handle Django validation errors
      const errors = error.responseData;
      const messages: string[] = [];
      
      Object.entries(errors).forEach(([field, value]) => {
        if (Array.isArray(value)) {
          messages.push(`${field}: ${value.join(', ')}`);
        } else if (typeof value === 'string') {
          messages.push(`${field}: ${value}`);
        } else {
          messages.push(`${field}: ${JSON.stringify(value)}`);
        }
      });
      
      return messages.join('\n');
    }
    
    return error?.message || 'An error occurred while updating the job';
  };

  // US states for dropdown
  const usStates = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  const isFormValid = formData.address_1 && formData.city && formData.state && formData.zip_code && formData.board && formData.board_status;

  if (!job) return null;

  return (
    <>
      <Modal open={open} onClose={confirmClose}>
        <ModalDialog size="lg" sx={{ overflow: 'auto', maxHeight: '90vh' }}>
        <ModalClose />
        <Typography level="h4">Edit Job</Typography>
        
        {updateMutation.isError && (
          <Alert color="danger" sx={{ mt: 2 }}>
            {getErrorMessage(updateMutation.error)}
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Stack direction="row" spacing={1} sx={{flexWrap:'wrap',my:1}}>{['Property','People','Claim'].map(section=><Button key={section} type="button" variant="soft" size="sm" onClick={()=>document.getElementById(`edit-job-${section.toLowerCase()}`)?.scrollIntoView({block:'start',behavior:'smooth'})}>{section}</Button>)}</Stack>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Basic Claim and job information */}
            <Typography id="edit-job-claim" level="title-md" sx={{ mt: 2 }}>
              Claim and job information
            </Typography>
            
            <FormControl>
              <FormLabel>Job Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => changeFormData({ ...formData, name: e.target.value })}
                placeholder="Enter job name (optional)"
                slotProps={{ input: { maxLength: 255 } }}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Claim Number</FormLabel>
              <Input
                value={formData.claim_number}
                onChange={(e) => changeFormData({ ...formData, claim_number: e.target.value })}
                placeholder="Enter claim number (optional)"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => changeFormData({ ...formData, description: e.target.value })}
                placeholder="Enter job description (optional)"
                minRows={3}
                maxRows={6}
              />
            </FormControl>

            {/* Claim details - the supplement and CoC documents read these,
                so filling them here stops them being re-typed per send */}
            <Grid container spacing={2}>
              <Grid xs={12} sm={6}><FormControl><FormLabel>Gate Code</FormLabel>
                <Input value={formData.gate_code || ''} onChange={(e) => changeFormData({ ...formData, gate_code: e.target.value })} />
              </FormControl></Grid>
              <Grid xs={12} sm={6}><FormControl><FormLabel>Gated Community</FormLabel>
                <Select value={formData.gated_community == null ? '' : String(formData.gated_community)}
                  onChange={(_, value) => changeFormData({ ...formData, gated_community: !value ? null : value === 'true' })}>
                  <Option value="">Not specified</Option><Option value="true">Yes</Option><Option value="false">No</Option>
                </Select>
              </FormControl></Grid>
            </Grid>

            <Typography level="title-md" sx={{ mt: 2 }}>
              Claim details
            </Typography>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Insurance Company</FormLabel>
                  <Input
                    value={formData.insurance_company}
                    onChange={(e) => changeFormData({ ...formData, insurance_company: e.target.value })}
                    placeholder="Carrier name (optional)"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Policy Number</FormLabel>
                  <Input
                    value={formData.policy_number}
                    onChange={(e) => changeFormData({ ...formData, policy_number: e.target.value })}
                    placeholder="Policy number (optional)"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Deductible ($)</FormLabel>
                  <Input
                    type="number"
                    value={formData.deductible_amount || ''}
                    onChange={(e) => changeFormData({ ...formData, deductible_amount: e.target.value })}
                    placeholder="0.00"
                    slotProps={{
                      input: {
                        min: 0,
                        step: 0.01,
                      },
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl>
                  <FormLabel>Date of Loss</FormLabel>
                  <Input
                    type="date"
                    value={formData.date_of_loss || ''}
                    onChange={(e) => changeFormData({ ...formData, date_of_loss: e.target.value })}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>Adjuster Name</FormLabel>
                  <Input
                    value={formData.adjuster_name}
                    onChange={(e) => changeFormData({ ...formData, adjuster_name: e.target.value })}
                    placeholder="Adjuster name (optional)"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>Adjuster Email</FormLabel>
                  <Input
                    type="email"
                    value={formData.adjuster_email}
                    onChange={(e) => changeFormData({ ...formData, adjuster_email: e.target.value })}
                    placeholder="adjuster@carrier.com"
                  />
                  <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Supplement and Certificate of Completion emails default to this address.
                  </Typography>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={4}>
                <FormControl>
                  <FormLabel>Adjuster Phone</FormLabel>
                  <Input
                    value={formData.adjuster_phone}
                    onChange={(e) => changeFormData({ ...formData, adjuster_phone: e.target.value })}
                    placeholder="(555) 555-5555"
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* Address Information */}
            <Typography id="edit-job-property" level="title-md" sx={{ mt: 2 }}>
              Property
            </Typography>

            <FormControl required>
              <FormLabel>Street Address</FormLabel>
              <AddressAutocompleteInput
                value={formData.address_1}
                onChange={(val) => changeFormData({ ...formData, address_1: val })}
                onAddressSelect={handleAddressSelect}
                placeholder="Enter street address"
                required
              />
            </FormControl>

            <FormControl>
              <FormLabel>Address Line 2</FormLabel>
              <Input
                value={formData.address_2}
                onChange={(e) => changeFormData({ ...formData, address_2: e.target.value })}
                placeholder="Apartment, suite, etc. (optional)"
              />
            </FormControl>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>City</FormLabel>
                  <Input
                    value={formData.city}
                    onChange={(e) => changeFormData({ ...formData, city: e.target.value })}
                    placeholder="Enter city"
                  />
                </FormControl>
              </Grid>

              <Grid xs={12} sm={3}>
                <FormControl required>
                  <FormLabel>State</FormLabel>
                  <Select
                    value={formData.state || null}
                    onChange={(_, value) => changeFormData({ ...formData, state: value || '' })}
                    placeholder="Select state"
                  >
                    {usStates.map((state) => (
                      <Option key={state} value={state}>
                        {state}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={3}>
                <FormControl required>
                  <FormLabel>ZIP Code</FormLabel>
                  <Input
                    value={formData.zip_code}
                    onChange={(e) => changeFormData({ ...formData, zip_code: e.target.value })}
                    placeholder="ZIP code"
                  />
                </FormControl>
              </Grid>
            </Grid>

            {/* Job Details */}
            <Typography level="title-md" sx={{ mt: 2 }}>
              Job Details
            </Typography>

            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>Board</FormLabel>
                  <Select
                    value={formData.board !== 0 ? formData.board : null}
                    onChange={(_, value) => {
                      changeFormData({
                        ...formData, 
                        board: value || 0,
                        board_status: 0 // Reset status when board changes
                      });
                    }}
                    placeholder="Select board"
                    renderValue={(selected) => {
                      if (!selected) return null;
                      const boardId = typeof selected === 'object' && 'value' in selected ? selected.value : selected;
                      const selectedBoard = boards.find(b => b.id === boardId);
                      if (!selectedBoard) return null;
                      
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: 1,
                              backgroundColor: selectedBoard.color,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                          {selectedBoard.name}
                        </Box>
                      );
                    }}
                  >
                    {(boards || []).map((board) => (
                      <Option key={board.id} value={board.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: 1,
                              backgroundColor: board.color,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                          {board.name}
                        </Box>
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid xs={12} sm={6}>
                <FormControl required>
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={formData.board_status !== 0 ? formData.board_status : null}
                    onChange={(_, value) => changeFormData({ ...formData, board_status: value || 0 })}
                    placeholder="Select status"
                    disabled={!formData.board}
                  >
                    {(statuses || [])
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((status) => (
                        <Option key={status.id} value={status.id}>
                          {status.name}
                        </Option>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FormControl>
              <FormLabel>Contract Total ($)</FormLabel>
              <Input
                type="number"
                value={formData.value || ''}
                onChange={(e) => 
                  changeFormData({
                    ...formData, 
                    value: e.target.value ? parseFloat(e.target.value) : undefined 
                  })
                }
                placeholder="0.00"
                slotProps={{
                  input: {
                    min: 0,
                    step: 0.01,
                  },
                }}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Build Date</FormLabel>
              <Input
                type="date"
                value={formData.build_date || ''}
                onChange={(e) => changeFormData({ ...formData, build_date: e.target.value || undefined })}
                placeholder="Select build date"
                disabled={!user?.is_organization_admin && !user?.can_schedule_builds}
              />
              {!user?.is_organization_admin && !user?.can_schedule_builds && (
                <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  You don't have permission to schedule build dates
                </Typography>
              )}
            </FormControl>

            {/* People · assignment */}
            <Typography level="title-md" sx={{ mt: 2 }}>
              People · assignment
            </Typography>

            <FormControl>
              <FormLabel>Assigned To</FormLabel>
              <Select
                value={formData.assigned_to?.toString() || ''}
                onChange={(_, value) => changeFormData({ ...formData, assigned_to: value ? parseInt(value) : undefined })}
                placeholder="Select a user"
                disabled={usersLoading}
                endDecorator={usersLoading ? <CircularProgress size="sm" /> : null}
              >
                <Option value="">None</Option>
                {(users || []).map((user) => (
                  <Option key={user.id} value={user.id.toString()}>
                    {user.first_name} {user.last_name}
                  </Option>
                ))}
              </Select>
            </FormControl>

            {/* Tags */}
            <FormControl>
              <FormLabel>Tags</FormLabel>
              <TagPicker
                value={selectedTagIds}
                onChange={(ids) => { setDirty(true); setSelectedTagIds(ids); }}
                availableTags={availableTags}
                onCreateTag={handleCreateTag}
                label="" // No label - parent FormControl provides it
              />
            </FormControl>

            {/* Contact Association */}
            <Typography id="edit-job-people" level="title-md" sx={{ mt: 2 }}>
              People · contacts
            </Typography>

            <FormControl>
              <FormLabel>Associated Contact</FormLabel>
              <Autocomplete
                placeholder="Search for a contact"
                value={
                  formData.associated_contact 
                    ? contacts.find((contact: any) => contact.id === formData.associated_contact) || null
                    : null
                }
                inputValue={contactSearch}
                onInputChange={(_, value) => setContactSearch(value)}
                onChange={(_, value) => {
                  // Attaching a customer to a job that has no address yet
                  // fills it from their primary address. Existing values are
                  // never overwritten - this is an edit form, so what is
                  // already on the job wins.
                  const address = contactAddressFields(value);
                  changeFormData(prev => applyPrefill(
                    { ...prev, associated_contact: value?.id || undefined },
                    address
                      ? {
                          address_1: address.address_1,
                          address_2: address.address_2,
                          city: address.city,
                          state: address.state,
                          zip_code: address.zip_code,
                        }
                      : {},
                  ));
                }}
                options={contacts}
                getOptionLabel={(option: any) => 
                  option.full_name || (option.first_name && option.last_name ? `${option.first_name} ${option.last_name}` : `Contact ${option.id}`)
                }
                renderOption={(props, option: any) => (
                  <AutocompleteOption {...props} key={option.id}>
                    <div>
                      <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                        {option.full_name || (option.first_name && option.last_name ? `${option.first_name} ${option.last_name}` : `Contact ${option.id}`)}
                      </Typography>
                      {option.email && (
                        <Typography level="body-xs" textColor="text.secondary" sx={{ fontSize: '0.75rem' }}>
                          {option.email}
                        </Typography>
                      )}
                    </div>
                  </AutocompleteOption>
                )}
                sx={{ width: '100%' }}
              />
            </FormControl>

            {/* Action Buttons */}
            <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 4, position: 'sticky', bottom: 0, bgcolor: 'background.surface', py: 2, zIndex: 2, flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="outlined"
                color="danger"
                startDecorator={<DeleteIcon />}
                onClick={() => setShowDeleteConfirm(true)}
                disabled={updateMutation.isPending || deleteMutation.isPending}
              >
                Delete Job
              </Button>
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" color="neutral" onClick={confirmClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={updateMutation.isPending}
                  disabled={!isFormValid}
                >
                  Save Changes
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </form>
        </ModalDialog>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Modal open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          aria-labelledby="delete-confirm-dialog"
          aria-describedby="delete-confirm-description"
        >
          <DialogTitle id="delete-confirm-dialog">
            <DeleteIcon />
            Confirm Delete
          </DialogTitle>
          <DialogContent id="delete-confirm-description">
            Are you sure you want to delete this job? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button
              variant="solid"
              color="danger"
              onClick={() => {
                deleteMutation.mutate();
                setShowDeleteConfirm(false);
              }}
              loading={deleteMutation.isPending}
            >
              Delete Job
            </Button>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};
